export type DebouncedEmitter<T> = {
  /** Emit `value` after the delay; replaces any pending emit. */
  schedule: (value: T) => void;
  /** Emit `value` immediately, dropping any pending emit. */
  flush: (value: T) => void;
  /** Emit the pending value immediately, if there is one. */
  flushPending: () => void;
  /** Drop any pending emit without emitting. */
  cancel: () => void;
};

/**
 * Debounced emit with immediate flush and cancel. Framework-free so the timer
 * contract is unit-testable; `SearchButtonInput` owns the React wiring.
 */
export function createDebouncedEmitter<T>(emit: (value: T) => void, delayMs: number): DebouncedEmitter<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingValue: T;

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = (value: T) => {
    cancel();
    pendingValue = value;
    timer = setTimeout(() => {
      timer = null;
      emit(value);
    }, delayMs);
  };

  const flush = (value: T) => {
    cancel();
    emit(value);
  };

  const flushPending = () => {
    if (timer) flush(pendingValue);
  };

  return { schedule, flush, flushPending, cancel };
}
